import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Star, Quote, ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_LABELS = {
  mariage: 'Mariage',
  famille: 'Famille',
  sejour: 'Sejour',
  seminaire: 'Seminaire',
  anniversaire: 'Anniversaire',
  google: 'Google',
};

function Stars({ count }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-4 h-4 ${i <= count ? 'fill-[#C4A962] text-[#C4A962]' : 'text-gray-200'}`} strokeWidth={1.5} />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const name = review.author_name || review.author || review.name || 'Anonyme';
  const eventType = review.event_type || review.source || '';
  const dateStr = review.date || '';
  const formattedDate = dateStr ? new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' }) : '';

  return (
    <div data-testid={`review-card-${review.id}`} className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] transition-all duration-300">
      <Quote className="w-6 h-6 text-[#C4A962]/30 mb-4" strokeWidth={1.5} />
      <Stars count={review.rating} />
      <p className="text-[#333] text-sm md:text-base leading-relaxed mt-4 mb-5">"{review.text}"</p>
      <div className="border-t border-[#E5E5E0] pt-4 flex items-center justify-between">
        <div>
          <p className="font-semibold text-[#2C3E30] text-sm">{name}</p>
          {formattedDate && <p className="text-[#888] text-xs mt-0.5">{formattedDate}</p>}
        </div>
        {eventType && (
          <span className="text-xs bg-[#F5F5F0] text-[#595959] px-3 py-1 rounded-full">
            {EVENT_LABELS[eventType] || eventType}
          </span>
        )}
      </div>
      {review.response_text && (
        <div className="mt-4 bg-[#F5F5F0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-3.5 h-3.5 text-[#8A9A5B]" />
            <span className="text-xs font-medium text-[#8A9A5B]">Reponse du Domaine</span>
          </div>
          <p className="text-[#595959] text-xs leading-relaxed">{review.response_text}</p>
        </div>
      )}
    </div>
  );
}

function EmbedSocialWidget({ widgetId }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!widgetId) return;
    if (!document.getElementById('EmbedSocialHashtagScript')) {
      const script = document.createElement('script');
      script.id = 'EmbedSocialHashtagScript';
      script.src = 'https://embedsocial.com/cdn/ht.js';
      document.head.appendChild(script);
    } else if (window.EmbedSocialHashtagWidget) {
      window.EmbedSocialHashtagWidget.init();
    }
  }, [widgetId]);

  if (!widgetId) return null;
  return (
    <div ref={containerRef}>
      <div className="embedsocial-hashtag" data-ref={widgetId}></div>
    </div>
  );
}

export default function ReviewsPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [widgetId, setWidgetId] = useState('');
  const [googleData, setGoogleData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/reviews`),
      axios.get(`${BACKEND_URL}/api/public/embedsocial-id`),
    ]).then(([revRes, embedRes]) => {
      setReviews(revRes.data || []);
      setWidgetId(embedRes.data?.widget_id || '');
    }).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${BACKEND_URL}/api/reviews/google`)
      .then(r => {
        const d = r.data;
        if (d?.configured && d?.place && (d.reviews?.length > 0 || d.place.review_count > 0)) setGoogleData(d);
      })
      .catch(() => {});
  }, []);

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : '0';

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navigation />

      {/* Hero banner */}
      <div className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#2C3E30] text-[#F2F0E9]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <span className="text-[#C4A962] text-sm font-medium tracking-[0.2em] uppercase">Temoignages</span>
          <h1 data-testid="reviews-page-title" className="font-['Playfair_Display'] text-3xl md:text-5xl mt-3 tracking-tight">
            Avis de nos hotes
          </h1>
          <p className="mt-4 text-[#F2F0E9]/70 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Decouvrez les experiences de ceux qui ont sejourne au Domaine du Pilat.
          </p>
          {reviews.length > 0 && (
            <div className="mt-8 inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className={`w-5 h-5 ${i <= Math.round(parseFloat(avgRating)) ? 'fill-[#C4A962] text-[#C4A962]' : 'text-white/30'}`} strokeWidth={1.5} />
                ))}
              </div>
              <span className="text-lg font-semibold">{avgRating}</span>
              <span className="text-sm text-[#F2F0E9]/60">/ 5 ({reviews.length} avis)</span>
            </div>
          )}
        </div>
      </div>

      {/* Reviews grid */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* EmbedSocial widget at top (if ID provided) */}
        {/* Google Places live reviews */}
        {googleData && (
          <div data-testid="google-reviews-section" className="mb-16">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div className="flex items-center gap-3">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#2C3E30]">Avis Google</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} className={`w-4 h-4 ${i <= Math.round(googleData.place.rating || 0) ? 'fill-[#C4A962] text-[#C4A962]' : 'text-[#E5E5E0]'}`} strokeWidth={1.5} />
                      ))}
                    </div>
                    <span data-testid="google-rating" className="text-sm font-semibold text-[#2C3E30]">{googleData.place.rating}</span>
                    <span className="text-sm text-[#595959]">({googleData.place.review_count} avis au total)</span>
                  </div>
                </div>
              </div>
              {googleData.place.google_maps_url && (
                <a
                  href={googleData.place.google_maps_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="google-all-reviews-link"
                  className="inline-flex items-center gap-1.5 text-sm text-[#4285F4] hover:underline"
                >
                  Voir tous les avis sur Google →
                </a>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {googleData.reviews.map((r, i) => (
                <div key={i} data-testid={`google-review-card-${i}`} className="bg-white rounded-2xl border border-[#E5E5E0] p-5 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    {r.author_photo ? (
                      <img src={r.author_photo} alt={r.author_name || 'Avis Google'} className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F5F5F0] flex items-center justify-center text-[#8A9A5B] font-semibold">
                        {(r.author_name || '?')[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <a href={r.author_url} target="_blank" rel="noopener noreferrer" className="font-medium text-sm text-[#2C3E30] hover:underline truncate block">
                        {r.author_name || 'Utilisateur Google'}
                      </a>
                      <span className="text-xs text-[#595959]">{r.relative_date}</span>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map(i2 => (
                      <Star key={i2} className={`w-3.5 h-3.5 ${i2 <= (r.rating || 0) ? 'fill-[#C4A962] text-[#C4A962]' : 'text-[#E5E5E0]'}`} strokeWidth={1.5} />
                    ))}
                  </div>
                  {r.text && <p className="text-sm text-[#595959] leading-relaxed line-clamp-6">{r.text}</p>}
                </div>
              ))}
            </div>
            <p className="text-xs text-[#595959]/60 mt-4 text-center">
              Les 5 avis les plus pertinents selon Google. Donnees fournies par Google.
            </p>
          </div>
        )}

        {/* EmbedSocial widget at top (if ID provided) */}
        {widgetId && (
          <div data-testid="embedsocial-section" className="mb-16">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2.5 shadow-sm">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-sm font-medium text-[#2C3E30]">Avis Google en direct</span>
              </div>
            </div>
            <EmbedSocialWidget widgetId={widgetId} />
          </div>
        )}

        {/* Placeholder if no widget ID and no Google data yet */}
        {!widgetId && !googleData && (
          <div data-testid="embedsocial-placeholder" className="mb-16 text-center py-10 bg-white rounded-2xl border border-dashed border-[#E5E5E0]">
            <svg viewBox="0 0 24 24" className="w-8 h-8 mx-auto mb-3 text-[#E5E5E0]" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            </svg>
            <p className="text-sm text-[#595959]">Les avis Google en direct seront affiches ici prochainement.</p>
          </div>
        )}

        {/* All reviews */}
        <div className="mb-8">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#2C3E30] mb-2">Tous les avis</h2>
          <p className="text-[#595959] text-sm">{reviews.length} temoignage{reviews.length > 1 ? 's' : ''}</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-6 h-6 border-2 border-[#8A9A5B] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E0]">
            <p className="text-[#595959]">Aucun avis pour le moment.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <ReviewCard key={r.id || i} review={r} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-16">
          <Button
            data-testid="reviews-page-reserve-btn"
            onClick={() => navigate('/#reservation')}
            className="rounded-full px-8 py-3 bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] transition-transform active:scale-95 text-base font-medium group"
          >
            Reserver votre sejour
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
